import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  useContextSources,
  useCreateContextSource,
  useDeleteContextSource,
  useToggleContextSourceActive,
} from "@/hooks/agent/useContextSource";
import IntegrationDetailsSheet from "./components/IntegrationDetailsSheet";
import IntegrationsGrid from "./components/IntegrationsGrid";
import IntegrationsHeader from "./components/IntegrationsHeader";
import IntegrationsModal from "./components/IntegrationsModal";
import { IntegrationFormState } from "./types";
import { buildCredentialValue, buildPayload } from "./utils";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const IntegrationsPage = () => {
  const { data = [], isLoading } = useContextSources();
  const sources = Array.isArray(data) ? data : [];
  const { mutate: createSource, isPending: isCreating } = useCreateContextSource();
  const { mutate: deleteSource, isPending: isDeleting } = useDeleteContextSource();
  const { mutate: toggleActive } = useToggleContextSourceActive();

  const [isConfiguring, setIsConfiguring] = useState(false);
  const [selectedType, setSelectedType] = useState<"text_document" | "api" | "mcp" | null>(null);
  const [formData, setFormData] = useState<IntegrationFormState>({});
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);

  const handleCreateSuccess = () => {
    setIsConfiguring(false);
    setSelectedType(null);
    setFormData({});
  };

  const handleCloseModal = () => {
    setIsConfiguring(false);
    setSelectedType(null);
    setFormData({});
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <div className="w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <IntegrationsHeader />
            <Button onClick={() => setIsConfiguring(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Integration
            </Button>

            <IntegrationsModal
              isOpen={isConfiguring}
              onClose={handleCloseModal}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              formData={formData}
              setFormData={setFormData}
              onSubmit={() => {
                const credentialValue = buildCredentialValue(
                  selectedType as "text_document" | "api" | "mcp",
                  formData,
                );

                createSource(buildPayload(selectedType, formData, credentialValue), {
                  onSuccess: handleCreateSuccess,
                });
              }}
              isCreating={isCreating}
            />
          </div>

          {sources.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="h-12 w-12 text-gray-400 mb-4">
                  <GenieIcon name="database" size={24} className="h-full w-full" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No integrations yet</h3>
                <p className="text-gray-600 text-center mb-4 max-w-sm">
                  Set up your first data source (API, MCP, or database) to allow your genies to
                  search private information
                </p>
              </CardContent>
            </Card>
          ) : (
            <IntegrationsGrid
              sources={sources}
              isDeleting={isDeleting}
              onSelectSource={setSelectedSourceId}
              onDelete={deleteSource}
              onToggleActive={toggleActive}
            />
          )}
        </div>
      </div>

      <IntegrationDetailsSheet
        sourceId={selectedSourceId}
        sources={sources}
        onClose={() => setSelectedSourceId(null)}
        onToggleActive={toggleActive}
      />
    </div>
  );
};

export default IntegrationsPage;
